import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, getDoc, getDocs, setDoc, Timestamp, runTransaction, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { NotificationData, NotificationCounts } from '../types/notification';
import { useAuth } from '../contexts/AuthContext';

// Type for cached notification data
interface CachedNotificationData {
  notifications: NotificationData[];
  counts: NotificationCounts;
  timestamp: number;
}

interface UseNotificationsResult {
  notifications: NotificationData[];
  counts: NotificationCounts;
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  isFromCache: boolean;
  isOffline: boolean;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
}

export const useNotifications = (): UseNotificationsResult => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [counts, setCounts] = useState<NotificationCounts>({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const { currentUser, authState } = useAuth();

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Helper function to get notification counts and sync user document
  const getNotificationCounts = useCallback(async (userId: string): Promise<NotificationCounts> => {
    try {
      const notificationsRef = collection(db, 'notifications', userId, 'notifications');
      const snapshot = await getDocs(notificationsRef);
      
      const total = snapshot.size;
      let unread = 0;
      const readStates: { [id: string]: boolean } = {};
      
      snapshot.forEach(doc => {
        const data = doc.data();
        readStates[doc.id] = data.read || false;
        if (!data.read) {
          unread++;
        }
      });
      

      
      // Check if user document needs to be synced
      const userNotificationDocRef = doc(db, 'notifications', userId);
      const userDoc = await getDoc(userNotificationDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const currentUserUnread = userData?.unread || 0;
        
        if (currentUserUnread !== unread) {
          // Update user document with correct unread count
          await updateDoc(userNotificationDocRef, {
            unread: unread,
            lastUpdated: Timestamp.now()
          });
        }
      } else if (total > 0) {
        // Create user document if it doesn't exist and we have notifications
        await setDoc(userNotificationDocRef, {
          unread: unread,
          lastUpdated: Timestamp.now()
        });
      }
      
      return { total, unread };
    } catch (error) {
      console.warn('Error getting notification counts:', error);
      return { total: 0, unread: 0 };
    }
  }, []);

  const fetchNotifications = useCallback(async (useCache = true) => {
    // Don't fetch if user is not authenticated or still loading
    if (!currentUser || authState.loading) {
      setLoading(authState.loading);
      setError(null);
      setNotifications([]);
      setCounts({ total: 0, unread: 0 });
      setIsFromCache(false);
      setLastUpdated(null);
      return;
    }

    const cacheKey = `notifications-${currentUser.uid}`;
    let cachedData: CachedNotificationData | null = null;
    let hasCachedData = false;
    
    // Always try to get cached data first
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        cachedData = JSON.parse(cached);
        hasCachedData = true;
      }
    } catch (error) {
      console.warn('Failed to parse cached notification data:', error);
    }
    
    // STEP 1: Load cached data immediately if available (for fast UI)
    // This ensures users see data quickly, regardless of online/offline status
    if (hasCachedData && cachedData) {
      if (!Array.isArray(cachedData.notifications)) {
        setError('Corrupt cache – no notifications array');
        setLoading(false);
        return;
      }
      
      setNotifications(cachedData.notifications);
      setCounts(cachedData.counts);
      setIsFromCache(true);
      setLastUpdated(new Date(cachedData.timestamp));
      setLoading(false); // Stop loading immediately when we have cached data
      setError(null);
      
      // If offline, we're done - return early
      if (isOffline) {
        return;
      }
      
      // IMPORTANT: Always fetch fresh data in background when online
      setBackgroundLoading(true); // Indicate background loading when we have cached data
    }

    // STEP 2: Always fetch fresh data from Firebase when online (cache-first, then fetch pattern)
    if (!isOffline) {
      // If we don't have cached data, this is initial loading
      if (!hasCachedData) {
        setLoading(true);
      } else {
        setBackgroundLoading(true);
      }
      
      try {
        // Fetch notifications directly from Firestore
        const notificationsRef = collection(db, 'notifications', currentUser.uid, 'notifications');
        const notificationsQuery = query(notificationsRef, orderBy('timestamp', 'desc'), limit(40));
        const snapshot = await getDocs(notificationsQuery);
        
        const notifications: NotificationData[] = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          notifications.push({
            id: doc.id,
            ...data,
            // Ensure timestamp is properly formatted
            timestamp: data.timestamp instanceof Timestamp 
              ? { seconds: data.timestamp.seconds, nanoseconds: data.timestamp.nanoseconds }
              : data.timestamp
          } as NotificationData);
        });

        // Get notification counts
        const counts = await getNotificationCounts(currentUser.uid);
        
        // Update with fresh data (this may update the UI if different from cache)
        setNotifications(notifications);
        setCounts(counts);
        setIsFromCache(false);
        setLastUpdated(new Date());
        setLoading(false);
        setBackgroundLoading(false);
        setError(null);

        // Cache the fresh data
        try {
          const dataToCache: CachedNotificationData = {
            notifications,
            counts,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (error) {
          console.warn('Failed to cache notification data:', error);
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        
        // If we already loaded cached data, don't show error - keep the cached data
        if (hasCachedData && cachedData) {
          setBackgroundLoading(false); // Clear background loading state
          // Don't change anything else - cached data is already displayed
        } else {
          // No cached data and network error - show error
          setLoading(false);
          setBackgroundLoading(false);
          
          // Try to use cached data as fallback if we haven't already
          if (hasCachedData && cachedData) {
            setNotifications(cachedData.notifications);
            setCounts(cachedData.counts);
            setIsFromCache(true);
            setLastUpdated(new Date(cachedData.timestamp));
            setError(null); // Clear error since we have fallback data
          } else {
            // Don't show error for 'not-found' or permission errors when collection doesn't exist yet
            if (err instanceof Error && (err.message.includes('not-found') || err.message.includes('permission'))) {
              setNotifications([]);
              setCounts({ total: 0, unread: 0 });
              setError(null);
            } else {
              setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
            }
          }
        }
      }
    } else if (!hasCachedData) {
      // Offline with no cached data - this is the only time we should show "Loading..."
      setLoading(false);
      setError('No internet connection and no cached data available');
    }
  }, [getNotificationCounts, currentUser, authState.loading, isOffline]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!currentUser?.uid) return;

    try {
      // Optimistic update: Mark as read locally immediately for fast UI
      const updatedNotifications = notifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      );
      
      const updatedCounts = {
        total: counts.total,
        unread: Math.max(0, counts.unread - 1)
      };
      
      setNotifications(updatedNotifications);
      setCounts(updatedCounts);
      
      // Update cache with optimistic data
      try {
        const cacheKey = `notifications-${currentUser.uid}`;
        const dataToCache: CachedNotificationData = {
          notifications: updatedNotifications,
          counts: updatedCounts,
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      } catch (error) {
        console.warn('Failed to update notification cache:', error);
      }

      // Now update in Firestore directly using a transaction to update both the notification and user document
      await runTransaction(db, async (transaction) => {
        const notificationRef = doc(db, 'notifications', currentUser.uid, 'notifications', notificationId);
        const userNotificationDocRef = doc(db, 'notifications', currentUser.uid);

        // Read the notification first to check if it's currently unread
        const notificationDoc = await transaction.get(notificationRef);
        
        if (!notificationDoc.exists()) {
          throw new Error(`Notification ${notificationId} not found`);
        }

        const notificationData = notificationDoc.data();
        
        // Only update if the notification is currently unread
        if (!notificationData?.read) {
          // Mark notification as read
          transaction.update(notificationRef, { read: true });

          // Update user notification document to decrease unread count
          const userNotificationDoc = await transaction.get(userNotificationDocRef);
          if (userNotificationDoc.exists()) {
            const currentData = userNotificationDoc.data();
            const currentUnread = currentData?.unread || 0;
            transaction.update(userNotificationDocRef, {
              unread: Math.max(0, currentUnread - 1),
              lastUpdated: Timestamp.now()
            });
          }
        }
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
      // Revert optimistic update on error by refetching
      await fetchNotifications(false);
      throw err;
    }
  }, [currentUser?.uid, notifications, counts, fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      // Optimistic update: Mark all as read locally immediately for fast UI
      const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));
      const updatedCounts = {
        total: counts.total,
        unread: 0
      };
      
      setNotifications(updatedNotifications);
      setCounts(updatedCounts);
      
      // Update cache with optimistic data
      try {
        const cacheKey = `notifications-${currentUser.uid}`;
        const dataToCache: CachedNotificationData = {
          notifications: updatedNotifications,
          counts: updatedCounts,
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      } catch (error) {
        console.warn('Failed to update notification cache:', error);
      }

      // Now update all notifications in Firestore directly using a batch write
      // First get all unread notifications, then update them
      const notificationsRef = collection(db, 'notifications', currentUser.uid, 'notifications');
      const userNotificationDocRef = doc(db, 'notifications', currentUser.uid);
      const unreadQuery = query(notificationsRef, where('read', '==', false));
      const unreadSnapshot = await getDocs(unreadQuery);
      
      // Use a transaction to update everything atomically
      await runTransaction(db, async (transaction) => {
        // Check if user notification document exists
        const userDoc = await transaction.get(userNotificationDocRef);
        
        // Mark all unread notifications as read
        unreadSnapshot.docs.forEach(doc => {
          transaction.update(doc.ref, { read: true });
        });

        // Set unread count to 0 in user document
        if (userDoc.exists()) {
          transaction.update(userNotificationDocRef, {
            unread: 0,
            lastUpdated: Timestamp.now()
          });
        } else {
          // Create the user document if it doesn't exist
          transaction.set(userNotificationDocRef, {
            unread: 0,
            lastUpdated: Timestamp.now()
          });
        }
      });
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      // Revert optimistic update on error by refetching
      await fetchNotifications(false);
      throw err;
    }
  }, [currentUser?.uid, notifications, counts, fetchNotifications]);

  const refresh = useCallback(async () => {
    await fetchNotifications(false); // Force fresh fetch on manual refresh
  }, [fetchNotifications]);

  // Auto-mark all as read when visiting notifications page
  const autoMarkAllAsRead = useCallback(async () => {
    if (!currentUser?.uid) {
      return;
    }

    try {
      // Only mark as read if there are unread notifications
      if (counts.unread > 0) {
        
        // Optimistic update: Mark all as read locally immediately
        const updatedNotifications = notifications.map(notification => ({ ...notification, read: true }));
        const updatedCounts = {
          total: counts.total,
          unread: 0
        };
        
        setNotifications(updatedNotifications);
        setCounts(updatedCounts);
        
        // Update cache with optimistic data
        try {
          const cacheKey = `notifications-${currentUser.uid}`;
          const dataToCache: CachedNotificationData = {
            notifications: updatedNotifications,
            counts: updatedCounts,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (error) {
          console.warn('Failed to update notification cache:', error);
        }

        // Send to Firestore in background
        try {
          // First get all unread notifications, then update them
          const notificationsRef = collection(db, 'notifications', currentUser.uid, 'notifications');
          const userNotificationDocRef = doc(db, 'notifications', currentUser.uid);
          const unreadQuery = query(notificationsRef, where('read', '==', false));
          const unreadSnapshot = await getDocs(unreadQuery);
          
          // Use a transaction to update everything atomically
          await runTransaction(db, async (transaction) => {
            // Check if user notification document exists
            const userDoc = await transaction.get(userNotificationDocRef);
            
            // Mark all unread notifications as read
            unreadSnapshot.docs.forEach(doc => {
              transaction.update(doc.ref, { read: true });
            });

            // Set unread count to 0 in user document
            if (userDoc.exists()) {
              transaction.update(userNotificationDocRef, {
                unread: 0,
                lastUpdated: Timestamp.now()
              });
            } else {
              // Create the user document if it doesn't exist
              transaction.set(userNotificationDocRef, {
                unread: 0,
                lastUpdated: Timestamp.now()
              });
            }
          });
        } catch (err) {
          console.error('Failed to auto-mark all notifications as read:', err);
          // Don't revert on server error - user has already "seen" the notifications
        }
      }
    } catch (err) {
      console.error('Error in auto mark all as read:', err);
    }
  }, [currentUser?.uid, counts.unread, notifications, counts.total]);

  useEffect(() => {
    if (currentUser?.uid) {
      // Always fetch fresh data to ensure latest updates (cache-first + background fetch pattern)
      fetchNotifications(false);
    }
  }, [currentUser?.uid]); // Only run when user changes

  // Auto-mark all as read after data is loaded and there are unread notifications
  useEffect(() => {
    if (!loading && currentUser?.uid && counts.unread > 0) {
      // Small delay to ensure UI has rendered before marking as read
      const timer = setTimeout(() => {
        autoMarkAllAsRead();
      }, 1000); // 1 second delay to let user see the notifications first

      return () => {
        clearTimeout(timer);
      };
    }
  }, [loading, currentUser?.uid, counts.unread]); // Removed autoMarkAllAsRead from dependencies

  return {
    notifications,
    counts,
    loading,
    backgroundLoading,
    error,
    isFromCache,
    isOffline,
    lastUpdated,
    refresh,
    markAsRead
  };
};